import { PROCEDURAL_RASTER } from "./proceduralPattern";

/**
 * Fuentes GLSL (ES 3.0 / WebGL2) y WGSL (WebGPU) del fondo procedural. Cada
 * función es el port línea a línea de `proceduralPattern.ts` (referencia CPU
 * = loop del DOM); los comentarios marcan la línea equivalente.
 *
 * Emulación del raster: el DOM/fallback calcula el patrón en un canvas de
 * 192×108 (texel (x,y) = f(x/W, y/H)) y lo estira a los bounds con filtro
 * bilineal + clamp-to-edge. El fragment hace lo mismo: pasa el UV a espacio
 * de texel (`uv·[W,H] − 0.5`), evalúa el patrón en los 4 texeles vecinos
 * (premultiplicado, como la textura subida) y mezcla con `fract`. Así el look
 * es el del raster (mismo blur de escalado), sin CPU ni subida por frame.
 *
 * Uniforms del vertex (los enlaza Pixi): `uProjectionMatrix`,
 * `uWorldTransformMatrix` (globales) y `uTransformMatrix`, `uColor`
 * (locales del mesh; `uColor` = tinte·alpha premultiplicado del container,
 * va como varying al fragment). Los del patrón viven en el grupo
 * `proceduralUniforms`: `uColorA`, `uColorB` (0..1), `uTime`, `uScale`,
 * `uOpacity`, `uPattern` (índice de `PROCEDURAL_PATTERN_INDEX`, como float:
 * el polyfill de `pixi.js/unsafe-eval` escribe los `i32` como float).
 */

const W = PROCEDURAL_RASTER.width.toFixed(1);
const H = PROCEDURAL_RASTER.height.toFixed(1);

export const PROCEDURAL_GLSL_VERTEX = /* glsl */ `#version 300 es
precision highp float;

in vec2 aPosition;
in vec2 aUV;

uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;
uniform vec4 uColor;

out vec2 vUV;
out vec4 vColor;

void main() {
  mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
  gl_Position = vec4((mvp * vec3(aPosition, 1.0)).xy, 0.0, 1.0);
  vUV = aUV;
  vColor = uColor;
}
`;

export const PROCEDURAL_GLSL_FRAGMENT = /* glsl */ `#version 300 es
precision highp float;
precision highp int;

in vec2 vUV;
in vec4 vColor;

uniform vec3 uColorA;
uniform vec3 uColorB;
uniform float uTime;
uniform float uScale;
uniform float uOpacity;
uniform float uPattern;

out vec4 finalColor;

const float RASTER_W = ${W};
const float RASTER_H = ${H};

// JS \`x % 1\` (signo del dividendo); \`mod()\` de GLSL sería siempre >= 0.
float jsMod1(float x) {
  return x - trunc(x);
}

// valueNoise.hash: (a*374761393 + b*668265263 + floor(t*100)*982451653) | 0
// en u32 con wrap (= ToInt32 del entero exacto); xorshift-multiply igual.
float hashU32(int a, int b, int t) {
  uint n = uint(a) * 374761393u + uint(b) * 668265263u + uint(t) * 982451653u;
  uint m = (n ^ (n >> 13u)) * 1274126177u;
  return float(m ^ (m >> 16u)) / 4294967296.0;
}

// valueNoise(x, y, t): hash en las 4 esquinas de la celda + smoothstep.
float valueNoise(float x, float y, float t) {
  float ix = floor(x);
  float iy = floor(y);
  float fx = x - ix;
  float fy = y - iy;
  int a = int(ix);
  int b = int(iy);
  int tt = int(floor(t * 100.0));
  float h00 = hashU32(a, b, tt);
  float h10 = hashU32(a + 1, b, tt);
  float h01 = hashU32(a, b + 1, tt);
  float h11 = hashU32(a + 1, b + 1, tt);
  float sx = fx * fx * (3.0 - 2.0 * fx);
  float sy = fy * fy * (3.0 - 2.0 * fy);
  return mix(mix(h00, h10, sx), mix(h01, h11, sx), sy);
}

// proceduralPixel(pattern, u, w, time, scale) → v 0..1.
float patternValue(float u, float w) {
  int pattern = int(uPattern + 0.5);
  if (pattern == 0) {
    // gradient-flow
    return 0.5 + 0.5 * sin((u * 4.0 + w * 4.0 + uTime) * uScale + sin(uTime * 0.7) * 2.0);
  }
  if (pattern == 1) {
    // plasma
    float sx = u * 6.0 * uScale;
    float sy = w * 6.0 * uScale;
    float v = (sin(sx + uTime) +
      sin(sy + uTime * 0.8) +
      sin((sx + sy) * 0.5 + uTime * 1.3) +
      sin(sqrt(sx * sx + sy * sy) + uTime)) / 4.0;
    return 0.5 + 0.5 * v;
  }
  if (pattern == 2) {
    // scanline
    float pos = jsMod1(uTime * 0.12);
    float d0 = abs(w - pos);
    float d = min(d0, 1.0 - d0);
    float bandScale = max(0.05, uScale);
    float band = exp(-(d * d) / (0.004 * bandScale));
    float lines = 0.5 + 0.5 * sin(w * 90.0 * bandScale);
    return band * (0.9 + 0.1 * lines);
  }
  if (pattern == 3) {
    // voronoi. scale 0: en JS cellSize = Infinity y ningún seed queda a
    // distancia finita → v = 0; acá se corta antes de dividir por 0.
    if (uScale <= 0.0) return 0.0;
    float cellSize = 0.18 / uScale;
    float cx = floor(u / cellSize);
    float cy = floor(w / cellSize);
    float minDist = 1.0;
    for (int dy = -1; dy <= 1; dy++) {
      for (int dx = -1; dx <= 1; dx++) {
        float nx = cx + float(dx);
        float ny = cy + float(dy);
        float seedX = (nx + sin(nx * 12.9898 + uTime * 0.4) * 0.5) * cellSize;
        float seedY = (ny + cos(ny * 78.233 + uTime * 0.4) * 0.5) * cellSize;
        float dd = length(vec2(u - seedX, w - seedY));
        minDist = min(minDist, dd);
      }
    }
    return min(1.0, minDist / cellSize);
  }
  // noise (default)
  float sx = u * 4.0 * uScale;
  float sy = w * 4.0 * uScale;
  return valueNoise(sx, sy, uTime * 0.3) * 0.6 +
    valueNoise(sx * 2.0, sy * 2.0, uTime * 0.5) * 0.3 +
    valueNoise(sx * 4.0, sy * 4.0, uTime * 0.7) * 0.1;
}

// Texel (x,y) del raster, premultiplicado: rgb = lerp(colorA, colorB, v),
// alpha = opacity·(scanline ? v : 1) (proceduralAlpha).
vec4 texel(float x, float y) {
  float v = patternValue(x / RASTER_W, y / RASTER_H);
  vec3 rgb = mix(uColorA, uColorB, v);
  float a = uOpacity * (int(uPattern + 0.5) == 2 ? v : 1.0);
  return vec4(rgb * a, a);
}

void main() {
  // Muestreo bilineal + clamp-to-edge del raster W×H estirado al quad.
  vec2 p = vUV * vec2(RASTER_W, RASTER_H) - 0.5;
  vec2 i = floor(p);
  vec2 f = p - i;
  float x0 = clamp(i.x, 0.0, RASTER_W - 1.0);
  float x1 = clamp(i.x + 1.0, 0.0, RASTER_W - 1.0);
  float y0 = clamp(i.y, 0.0, RASTER_H - 1.0);
  float y1 = clamp(i.y + 1.0, 0.0, RASTER_H - 1.0);
  vec4 c = mix(
    mix(texel(x0, y0), texel(x1, y0), f.x),
    mix(texel(x0, y1), texel(x1, y1), f.x),
    f.y
  );
  finalColor = c * vColor;
}
`;

/**
 * WGSL: un solo módulo con ambos entry points (`mainVert` / `mainFrag`).
 * Grupos 0/1 = globales/locales de Pixi (los auto-asigna por nombre); grupo 2
 * = `proceduralUniforms` (mismo orden de campos que el UniformGroup del
 * renderer: Pixi arma el UBO por orden de declaración).
 */
export const PROCEDURAL_WGSL = /* wgsl */ `
struct GlobalUniforms {
  uProjectionMatrix: mat3x3<f32>,
  uWorldTransformMatrix: mat3x3<f32>,
  uWorldColorAlpha: vec4<f32>,
  uResolution: vec2<f32>,
}

struct LocalUniforms {
  uTransformMatrix: mat3x3<f32>,
  uColor: vec4<f32>,
  uRound: f32,
}

struct ProceduralUniforms {
  uColorA: vec3<f32>,
  uColorB: vec3<f32>,
  uTime: f32,
  uScale: f32,
  uOpacity: f32,
  uPattern: f32,
}

@group(0) @binding(0) var<uniform> globalUniforms: GlobalUniforms;
@group(1) @binding(0) var<uniform> localUniforms: LocalUniforms;
@group(2) @binding(0) var<uniform> proceduralUniforms: ProceduralUniforms;

const RASTER_W: f32 = ${W};
const RASTER_H: f32 = ${H};

struct VertexOutput {
  @builtin(position) position: vec4<f32>,
  @location(0) vUV: vec2<f32>,
  @location(1) vColor: vec4<f32>,
}

@vertex
fn mainVert(
  @location(0) aPosition: vec2<f32>,
  @location(1) aUV: vec2<f32>,
) -> VertexOutput {
  let mvp = globalUniforms.uProjectionMatrix * globalUniforms.uWorldTransformMatrix * localUniforms.uTransformMatrix;
  var vout: VertexOutput;
  vout.position = vec4<f32>((mvp * vec3<f32>(aPosition, 1.0)).xy, 0.0, 1.0);
  vout.vUV = aUV;
  vout.vColor = localUniforms.uColor;
  return vout;
}

// JS \`x % 1\` (signo del dividendo).
fn jsMod1(x: f32) -> f32 {
  return x - trunc(x);
}

// valueNoise.hash en u32 exacto (ver GLSL).
fn hashU32(a: i32, b: i32, t: i32) -> f32 {
  let n: u32 = bitcast<u32>(a) * 374761393u + bitcast<u32>(b) * 668265263u + bitcast<u32>(t) * 982451653u;
  let m: u32 = (n ^ (n >> 13u)) * 1274126177u;
  return f32(m ^ (m >> 16u)) / 4294967296.0;
}

// valueNoise(x, y, t).
fn valueNoise(x: f32, y: f32, t: f32) -> f32 {
  let ix = floor(x);
  let iy = floor(y);
  let fx = x - ix;
  let fy = y - iy;
  let a = i32(ix);
  let b = i32(iy);
  let tt = i32(floor(t * 100.0));
  let h00 = hashU32(a, b, tt);
  let h10 = hashU32(a + 1, b, tt);
  let h01 = hashU32(a, b + 1, tt);
  let h11 = hashU32(a + 1, b + 1, tt);
  let sx = fx * fx * (3.0 - 2.0 * fx);
  let sy = fy * fy * (3.0 - 2.0 * fy);
  return mix(mix(h00, h10, sx), mix(h01, h11, sx), sy);
}

// proceduralPixel(pattern, u, w, time, scale) → v 0..1.
fn patternValue(u: f32, w: f32) -> f32 {
  let uTime = proceduralUniforms.uTime;
  let uScale = proceduralUniforms.uScale;
  let uPattern = i32(proceduralUniforms.uPattern + 0.5);
  if (uPattern == 0) {
    // gradient-flow
    return 0.5 + 0.5 * sin((u * 4.0 + w * 4.0 + uTime) * uScale + sin(uTime * 0.7) * 2.0);
  }
  if (uPattern == 1) {
    // plasma
    let sx = u * 6.0 * uScale;
    let sy = w * 6.0 * uScale;
    let v = (sin(sx + uTime) +
      sin(sy + uTime * 0.8) +
      sin((sx + sy) * 0.5 + uTime * 1.3) +
      sin(sqrt(sx * sx + sy * sy) + uTime)) / 4.0;
    return 0.5 + 0.5 * v;
  }
  if (uPattern == 2) {
    // scanline
    let pos = jsMod1(uTime * 0.12);
    let d0 = abs(w - pos);
    let d = min(d0, 1.0 - d0);
    let bandScale = max(0.05, uScale);
    let band = exp(-(d * d) / (0.004 * bandScale));
    let lines = 0.5 + 0.5 * sin(w * 90.0 * bandScale);
    return band * (0.9 + 0.1 * lines);
  }
  if (uPattern == 3) {
    // voronoi (scale 0 → v = 0, ver GLSL)
    if (uScale <= 0.0) {
      return 0.0;
    }
    let cellSize = 0.18 / uScale;
    let cx = floor(u / cellSize);
    let cy = floor(w / cellSize);
    var minDist = 1.0;
    for (var dy: i32 = -1; dy <= 1; dy++) {
      for (var dx: i32 = -1; dx <= 1; dx++) {
        let nx = cx + f32(dx);
        let ny = cy + f32(dy);
        let seedX = (nx + sin(nx * 12.9898 + uTime * 0.4) * 0.5) * cellSize;
        let seedY = (ny + cos(ny * 78.233 + uTime * 0.4) * 0.5) * cellSize;
        let dd = length(vec2<f32>(u - seedX, w - seedY));
        minDist = min(minDist, dd);
      }
    }
    return min(1.0, minDist / cellSize);
  }
  // noise (default)
  let sx = u * 4.0 * uScale;
  let sy = w * 4.0 * uScale;
  return valueNoise(sx, sy, uTime * 0.3) * 0.6 +
    valueNoise(sx * 2.0, sy * 2.0, uTime * 0.5) * 0.3 +
    valueNoise(sx * 4.0, sy * 4.0, uTime * 0.7) * 0.1;
}

// Texel (x,y) del raster, premultiplicado (ver GLSL).
fn texel(x: f32, y: f32) -> vec4<f32> {
  let v = patternValue(x / RASTER_W, y / RASTER_H);
  let rgb = mix(proceduralUniforms.uColorA, proceduralUniforms.uColorB, v);
  let a = proceduralUniforms.uOpacity * select(1.0, v, i32(proceduralUniforms.uPattern + 0.5) == 2);
  return vec4<f32>(rgb * a, a);
}

@fragment
fn mainFrag(
  @location(0) vUV: vec2<f32>,
  @location(1) vColor: vec4<f32>,
) -> @location(0) vec4<f32> {
  // Muestreo bilineal + clamp-to-edge del raster W×H estirado al quad.
  let p = vUV * vec2<f32>(RASTER_W, RASTER_H) - 0.5;
  let i = floor(p);
  let f = p - i;
  let x0 = clamp(i.x, 0.0, RASTER_W - 1.0);
  let x1 = clamp(i.x + 1.0, 0.0, RASTER_W - 1.0);
  let y0 = clamp(i.y, 0.0, RASTER_H - 1.0);
  let y1 = clamp(i.y + 1.0, 0.0, RASTER_H - 1.0);
  let c = mix(
    mix(texel(x0, y0), texel(x1, y0), f.x),
    mix(texel(x0, y1), texel(x1, y1), f.x),
    f.y
  );
  return c * vColor;
}
`;
